import { Component, EventEmitter, Output, Input, OnDestroy, effect } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ChatCliRepService } from '../service/chat-cli-rep.service';
import { AuthService } from '../../../../../../core/service/auth.service';
import { MessageCliRep } from '../interfaces/chat-clip-rep.interface';

@Component({
  selector: 'app-chat-input',
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="border-t border-gray-200 p-2 sm:p-3 lg:p-4 bg-white rounded-b-lg">
      <!-- Preview de imagen seleccionada -->
      @if (selectedImage) {
        <div class="mb-2 sm:mb-3 relative inline-block">
          <img [src]="selectedImage"
               alt="Vista previa"
               class="max-w-24 max-h-24 sm:max-w-32 sm:max-h-32 rounded-lg border border-gray-300">
          <button (click)="removeImage()"
                  class="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-red-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors">
            ×
          </button>
        </div>
      }

      <div class="flex items-end space-x-1 sm:space-x-2">
        <!-- Input de archivo oculto -->
        <input #fileInput
               type="file"
               accept="image/*"
               (change)="onImageSelected($event)"
               class="hidden">

        <!-- Botón de adjuntar imagen -->
        <button (click)="fileInput.click()"
                class="p-1.5 sm:p-2 text-gray-500 hover:text-yellow-500 transition-colors flex-shrink-0">
          <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
          </svg>
        </button>

        <!-- Input de texto -->
        <div class="flex-1 relative ">
          <input [(ngModel)]="messageText"
                 (keydown.enter)="sendMessage()"
                 (input)="onTyping()"
                 placeholder="Escribe tu mensaje..."
                 class="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm border border-yellow-200 rounded-full focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none">
        </div>

        <!-- Botón de enviar -->
        <button (click)="sendMessage()"
                [disabled]="!canSend()"
                class="bg-yellow-500  text-white p-2 sm:p-3 rounded-full hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex-shrink-0">
          <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"></path>
          </svg>
        </button>
      </div>

      <p class="text-xs text-gray-500 mt-1 sm:mt-2 hidden sm:block">Escribe Enter para enviar</p>
    </div>
  `
})
export class ChatInputComponent implements OnDestroy {
  @Output() messageSent = new EventEmitter<{text: string, image?: string}>();
  @Output() typing = new EventEmitter<{senderId: number, receiverId: number, isTyping: boolean}>();
  @Output() messageCliRep = new EventEmitter<{data:MessageCliRep}>();
  @Output() fileSelected = new EventEmitter<File>();

  @Input() senderId!: number;
  @Input() receiverId!: number;
  @Input() orderId!: number;

  messageText: string = '';
  selectedImage: string | null = null;
  selectedFile: File | null = null;
  private typingTimeout: any;
  nombre:string='';

constructor(
  private authService: AuthService
){
  this.nombre=this.authService.getUser()?.ua_name + ' ' + this.authService.getUser()?.ua_lastname;
  effect(() => {

  })
}
  onImageSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selectedImage = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Solo guardar el archivo, no enviarlo aún
      this.selectedFile = file;
    }
  }

  removeImage() {
    this.selectedImage = null;
    this.selectedFile = null;
  }

  canSend(): boolean {
    return this.messageText.trim().length > 0 || this.selectedFile !== null;
  }

  onTyping() {
    // Emitir que está escribiendo
    this.typing.emit({
      senderId: this.senderId,
      receiverId: this.receiverId,
      isTyping: true
    });

    // Limpiar timeout anterior
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // Establecer timeout para dejar de escribir
    this.typingTimeout = setTimeout(() => {
      this.typing.emit({
        senderId: this.senderId,
        receiverId: this.receiverId,
        isTyping: false
      });
    }, 1000); // Dejar de escribir después de 1 segundo de inactividad
  }

  sendMessage() {
    if (this.canSend()) {
      // Emitir que dejó de escribir al enviar mensaje
      this.typing.emit({
        senderId: this.senderId,
        receiverId: this.receiverId,
        isTyping: false
      });

      // Enviar archivo si hay uno seleccionado
      if (this.selectedFile) {
        this.fileSelected.emit(this.selectedFile);
      }

      // Solo enviar mensaje de texto si hay texto
      if (this.messageText.trim()) {
        const messageData = {
          data:{
            senderId: this.senderId,
            senderUsername: this.nombre,
            receiverId: this.receiverId,
            orderId: this.orderId.toString(),
            content: this.messageText,
            type: 'TEXT' as const,
            fileName: '',
            fileUrl: '',
            fileSize: 0,
            mimeType: '',
            timestamp: new Date(),
            isClient: true,
          }
        };

        this.messageCliRep.emit(messageData);
        // console.log('mensaje enviando', messageData);
      }

      // Limpiar después de enviar
      this.messageText = '';
      this.selectedImage = null;
      this.selectedFile = null;

      // Limpiar timeout
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
    }
  }

  ngOnDestroy() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  }
}
